<script setup lang="ts">
import { AddIcon, CollectionsAddIcon, DismissIcon } from '@blpde/nx-icons';
import { computed, inject, watch } from 'vue';
import { i18nInjectionKey } from '../internal';
import { NxToggleGroup } from '../ToggleGroup';
import { NxSelectInput } from '../Inputs';
import NxTableFilterConditionInputs from './NxTableFilterConditionInputs.vue';
import {
  isFilterGroup,
  restrictedFilterOperators,
  type NxTableFilterColumnMeta,
  type NxTableFilterGroup,
  type NxTableFilterLeaf,
} from './filterModel';

/**
 * One and/or group in the filter wizard. Renders its leaves (column +
 * condition) and nested groups recursively — groups may be arbitrarily deep.
 */
const props = withDefaults(
  defineProps<{
    columns: NxTableFilterColumnMeta[];
    /** Root group cannot remove itself. */
    removable?: boolean;
    depth?: number;
  }>(),
  {
    removable: false,
    depth: 0,
  }
);

const group = defineModel<NxTableFilterGroup>({ required: true });

const emit = defineEmits<{ remove: [] }>();

const i18n = inject(i18nInjectionKey)!;

const columnById = computed(
  () => new Map(props.columns.map((column) => [column.columnId, column]))
);

/**
 * "or" between conditions of ONE column that does not offer it (`filterModifiers`
 * without 'or') is not offered: a group whose children are all leaves of such a single
 * column is "and" only. A group across columns, or with a nested group, is free.
 */
const orForbidden = computed(() => {
  const leaves = group.value.children;
  if (leaves.length === 0 || leaves.some((child) => isFilterGroup(child))) return false;
  const ids = new Set(leaves.map((child) => (child as NxTableFilterLeaf).columnId));
  if (ids.size !== 1) return false;
  const modifiers = columnById.value.get([...ids][0])?.modifiers;
  return !!modifiers && modifiers.length > 0 && !modifiers.includes('or');
});

// A stored or flipped "or" of such a group reads as "and".
watch(
  orForbidden,
  (forbidden) => {
    if (forbidden && group.value.modifier === 'or') group.value.modifier = 'and';
  },
  { immediate: true }
);

const modifierOptions = computed(() =>
  [
    { value: 'and' as const, label: i18n.t('common.table.filter.and') },
    { value: 'or' as const, label: i18n.t('common.table.filter.or') },
  ].filter((option) => option.value === 'and' || !orForbidden.value)
);

const columnIds = computed(() => props.columns.map((column) => column.columnId));
const columnLabel = (columnId: string) => columnById.value.get(columnId)?.displayName ?? columnId;

function metaOf(leaf: NxTableFilterLeaf): NxTableFilterColumnMeta {
  return (
    columnById.value.get(leaf.columnId) ?? {
      columnId: leaf.columnId,
      displayName: leaf.columnId,
      kind: 'string',
    }
  );
}

function addCondition() {
  const first = props.columns[0];
  if (!first) return;
  group.value.children.push({
    columnId: first.columnId,
    condition: { operator: restrictedFilterOperators(first.kind, first.operators)[0] },
  });
}

function addGroup() {
  group.value.children.push({
    modifier: group.value.modifier === 'and' ? 'or' : 'and',
    children: [],
  });
  addConditionTo(group.value.children.length - 1);
}

function addConditionTo(index: number) {
  const child = group.value.children[index];
  const first = props.columns[0];
  if (!child || !isFilterGroup(child) || !first) return;
  child.children.push({
    columnId: first.columnId,
    condition: { operator: restrictedFilterOperators(first.kind, first.operators)[0] },
  });
}

function removeChild(index: number) {
  group.value.children.splice(index, 1);
}

function setLeafColumn(leaf: NxTableFilterLeaf, columnId: string | null) {
  if (!columnId || columnId === leaf.columnId) return;
  const meta = columnById.value.get(columnId);
  leaf.columnId = columnId;
  leaf.condition = {
    operator: restrictedFilterOperators(meta?.kind ?? 'string', meta?.operators)[0],
  };
}
</script>

<template>
  <div :class="['wizard-group', { nested: props.depth > 0 }]">
    <div class="group-head">
      <NxToggleGroup
        :model-value="group.modifier"
        :options="modifierOptions"
        height="xs"
        @update:model-value="(m) => (group.modifier = m)"
      />
      <span class="group-hint">
        {{
          i18n.t(
            group.modifier === 'and'
              ? 'common.table.filter.wizard.and_hint'
              : 'common.table.filter.wizard.or_hint'
          )
        }}
      </span>
      <span class="head-spacer"></span>
      <button
        v-if="props.removable"
        type="button"
        class="icon-action"
        :title="i18n.t('common.table.filter.wizard.remove_group')"
        @click="emit('remove')"
      >
        <DismissIcon />
      </button>
    </div>

    <div v-if="group.children.length === 0" class="group-empty">
      {{ i18n.t('common.table.filter.wizard.empty_group') }}
    </div>

    <template v-for="(child, index) in group.children" :key="index">
      <NxTableFilterWizardGroup
        v-if="isFilterGroup(child)"
        :model-value="child"
        :columns="props.columns"
        removable
        :depth="props.depth + 1"
        @remove="removeChild(index)"
      />
      <div v-else class="leaf-row">
        <NxSelectInput
          class="leaf-column"
          :model-value="[child.columnId]"
          :options="columnIds"
          :option-text="columnLabel"
          :placeholder="i18n.t('common.table.filter.wizard.column')"
          height="xs"
          @update:model-value="(v: (string | null)[]) => setLeafColumn(child, v[0] ?? null)"
        >
          <template #option="{ option }">{{ columnLabel(option) }}</template>
        </NxSelectInput>
        <NxTableFilterConditionInputs
          v-model="child.condition"
          :kind="metaOf(child).kind"
          :options="metaOf(child).options"
          :option-label="metaOf(child).optionLabel"
          :allowed-operators="metaOf(child).operators"
        />
        <button
          type="button"
          class="icon-action"
          :title="i18n.t('common.table.filter.remove_condition')"
          @click="removeChild(index)"
        >
          <DismissIcon />
        </button>
      </div>
    </template>

    <div class="group-actions">
      <button type="button" class="text-action" @click="addCondition">
        <AddIcon />
        {{ i18n.t('common.table.filter.add_condition') }}
      </button>
      <button type="button" class="text-action" @click="addGroup">
        <CollectionsAddIcon />
        {{ i18n.t('common.table.filter.wizard.add_group') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.wizard-group {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);

  &.nested {
    background: var(--nxt-brand-subtle-bg);
    margin-left: var(--nxt-space-md);
  }
}

.group-head {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
}

.group-hint {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}

.head-spacer {
  flex: 1 1 auto;
}

.group-empty {
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
  padding: var(--nxt-space-2xs) 0;
}

.leaf-row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
}

.leaf-column {
  flex: 0 0 calc(var(--nxt-size-unit) * 10);
  min-width: 0;
}

.icon-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: var(--nxt-control-size-xs);
  height: var(--nxt-control-size-xs);
  padding: 0;
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-muted);
  cursor: pointer;

  &:hover {
    background-color: var(--nxt-brand-subtle-bg);
    color: var(--nxt-fg);
  }
}

.group-actions {
  display: flex;
  gap: var(--nxt-space-xs);
}

.text-action {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-2xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-brand-bg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  cursor: pointer;

  &:hover {
    background-color: var(--nxt-brand-subtle-bg);
  }
}
</style>
